/// <amd-module name="Test/Analytics/Widgets/PointHistoryWidget/PointHistoryWidget" />
import * as React from "react";

import { IVssComponentContext } from "VSS/Platform/Layout";
import { IVssContributionService } from "VSS/Platform/Contribution";
import * as Date_Utils from "VSSUI/Utilities/Date";
import { ObservableValue } from "VSS/Core/Observable";
import { Observer } from "VSSUI/Observer";

import { ITestProgressReportWidgetProps } from "Test/ManualTestReports/TestReportsCommon/ITestProgressReportWidgetProps";
import { BaseWidgetComponent } from "Test/ManualTestReports/TestReportsCommon/BaseWidgetComponent";
import { ITestManagementSettings, IPointHistoryOutcomeTrendState, IFilterData } from "Test/ManualTestReports/TestReportsCommon/Contracts";
import { PointHistoryWidgetSource } from "Test/Analytics/Widgets/PointHistoryWidget/PointHistoryWidgetSource";
import { ITestProgressReportFilterService } from "Test/ManualTestReports/TestReportsCommon/TestProgressReportFilterService";
import { PointHistoryCommands } from "Test/Analytics/Widgets/PointHistoryWidget/PointHistoryWidgetConstants";
import { PointHistoryInnerWidget } from "Test/Analytics/Widgets/PointHistoryWidget/PointHistoryInnerWidget";
import * as TelemetryService from "Test/ManualTestReports/TestReportsCommon/TelemetryService";
import * as TelemetryConstants from "Test/ManualTestReports/TestReportsCommon/TelemetryConstants";

export interface IPointHistoryWidgetProps extends ITestProgressReportWidgetProps {
}

export interface IPointHistoryWidgetState {
    loading: boolean;
    errorText?: string;
    historyData: IPointHistoryOutcomeTrendState;
}

export class PointHistoryWidget extends BaseWidgetComponent<IPointHistoryWidgetProps, IPointHistoryWidgetState, IVssComponentContext> {
    private _testProgressFilterService: ITestProgressReportFilterService;
    private _source: PointHistoryWidgetSource;
    private _filterData?: IFilterData;
    private readonly c_testPlanSettingsDataProviderId = "ms.vss-test-analytics-web.testplan-settings-provider";
    private _selectedCommand: ObservableValue<PointHistoryCommands> = new ObservableValue<PointHistoryCommands>(PointHistoryCommands.Last14Days);
    private _fromDate: ObservableValue<Date> = new ObservableValue<Date>(new Date(new Date().getTime() - 14*Date_Utils.day));
    private _toDate: ObservableValue<Date> = new ObservableValue<Date>(new Date());

    constructor(props: IPointHistoryWidgetProps, context: IVssComponentContext) {
        super(props);

        this._source = new PointHistoryWidgetSource("pointHistoryWidgetSource", context.pageContext);
        this.state = { loading: true } as IPointHistoryWidgetState;
        this._initializePointHistoryWidgetProps(context);
        this._testProgressFilterService = context.pageContext.getService<ITestProgressReportFilterService>("ITestProgressReportFilterService");
    }

    public componentWillMount() {
        this._testProgressFilterService.subscribe(this._onFilterUpdated);
    }

    public componentWillUnmount() {
        this._testProgressFilterService.unsubscribe(this._onFilterUpdated);
    }

    private _onFilterUpdated = (filterData: IFilterData) => {
        this._filterData = filterData;
        this.setState({
            loading: true
        });
        this._initializePointHistoryWidgetProps(this.context);
    }

    private _initializePointHistoryWidgetProps(context: IVssComponentContext) {
        var initializePromise = this._source.getPointHistoryWidgetProps(this._selectedCommand.value, this._filterData, this._getLastSelectedPlanId(context), this._fromDate.value, this._toDate.value);
        initializePromise.then((props: IPointHistoryOutcomeTrendState) => {
            this.setState({
                loading: false,
                historyData: props,
                errorText: undefined
            });
            this._publishWidgetLoadedEvent();
        }, (e: Error) => {
            this.setState({
                loading: false,
                errorText: e.message
            });
            this._publishWidgetLoadedEvent();
        });
    }

    private _publishWidgetLoadedEvent() {
        TelemetryService.publishEvent(
            this.context.pageContext,
            TelemetryConstants.Feature_ProgressReport,
            {
                errorText: this.state.errorText,
                selectedCommand: PointHistoryCommands[this._selectedCommand.value],
                numberOfDays: Math.ceil((this._toDate.value.getTime() - this._fromDate.value.getTime()) / Date_Utils.day),
                fromDate: this._fromDate.value.toISOString(),
                toDate: this._toDate.value.toISOString(),
                event: TelemetryConstants.Event_OutcomeTrendWidgetLoaded
            });
    }

    public render() {
        this.logRenderingStart();

        return (
            <Observer selectedCommand={this._selectedCommand}>
                {(props: {selectedCommand: PointHistoryCommands}) => {
                    return <PointHistoryInnerWidget 
                                widgetName={this.props.widgetName}
                                onRenderingStart={this.props.onRenderingStart}
                                onRenderingEnd={this.props.onRenderingEnd}
                                loading={this.state.loading}
                                errorText={this.state.errorText}
                                historyData={this.state.historyData}
                                selectedCommand={props.selectedCommand}
                                fromDate={this._fromDate}
                                toDate={this._toDate}
                                onCommandBarSubMenuItemSelected={this._onCommandBarSubMenuItemSelected}
                            />;
                }}
            </Observer>
        );
    }

    protected isLoading(): boolean {
        return this.state.loading;
    }

    private _onCommandBarSubMenuItemSelected = (command: PointHistoryCommands) => {
        if (this._selectedCommand.value === command && command !== PointHistoryCommands.CustomDate) {
            return;
        }
        this._selectedCommand.value = command;
        // setState of selectedCommand is required for render method where as _selectedCommand is required for OData call.
        this.setState({
            loading: true
        });
        this._initializePointHistoryWidgetProps(this.context);
    }

    private _getLastSelectedPlanId(context: IVssComponentContext): number | undefined {
        const contributionService = context.pageContext.getService<IVssContributionService>("IVssContributionService");
        let testManagementSettingsData: ITestManagementSettings = contributionService.getData<ITestManagementSettings>(this.c_testPlanSettingsDataProviderId)!;
        return testManagementSettingsData ? testManagementSettingsData.selectedPlanId: undefined;
    }
}